import { useState } from 'react';
import { Badge } from '@/components/ui';
import { DataTablePagination } from '@/components/custom';
import { AdminMessageStatusEnum, MessageAudienceLabelEnum, MessageAudienceEnum } from '@/enums';
import type { IAdminMessage, IMessageDetailModalProps } from '@/interfaces';
import { useRequestGetAdminMessages } from '@/hooks/httpRequest';
import { useQueryParams } from '@/hooks';
import MessageDetailModal from './MessageDetailModal';
import dayjs from 'dayjs';
import { cn } from '@/lib/utils';
import { RELIGION_LABELS, countryName } from '../constants';

const STATUS_CLASS: Record<AdminMessageStatusEnum, string> = {
  [AdminMessageStatusEnum.COMPLETED]: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  [AdminMessageStatusEnum.PENDING]: 'bg-amber-100 text-amber-700 border-amber-200',
  [AdminMessageStatusEnum.CANCELLED]: 'bg-gray-100 text-gray-600 border-gray-200',
  [AdminMessageStatusEnum.ERROR]: 'bg-red-100 text-red-700 border-red-200'
};

const STATUS_LABEL: Record<AdminMessageStatusEnum, string> = {
  [AdminMessageStatusEnum.COMPLETED]: 'Envoyé',
  [AdminMessageStatusEnum.ERROR]: 'Erreur',
  [AdminMessageStatusEnum.PENDING]: 'En cours',
  [AdminMessageStatusEnum.CANCELLED]: 'Annulé'
};

const audienceLabel = (recipients: string[]): string => {
  if (recipients.length === 1) {
    if (recipients[0] === MessageAudienceEnum.ALL) return MessageAudienceLabelEnum.ALL;
    if (recipients[0] === MessageAudienceEnum.ALL_IOS) return MessageAudienceLabelEnum.ALL_IOS;
    if (recipients[0] === MessageAudienceEnum.ALL_ANDROID) return MessageAudienceLabelEnum.ALL_ANDROID;
  }
  return `${recipients.length} utilisateur${recipients.length > 1 ? 's' : ''}`;
};

const filtersLabel = (message: IAdminMessage): string | null => {
  const parts: string[] = [];

  if (message.genderFilter?.length) {
    const genders = message.genderFilter.map(g => g === 'MALE' ? 'H' : 'F').join('+');
    parts.push(genders);
  }

  if (message.religionFilter?.length) {
    const religions = message.religionFilter.length === 1
      ? (RELIGION_LABELS[message.religionFilter[0]] ?? message.religionFilter[0])
      : `${message.religionFilter.length} religions`;
    parts.push(religions);
  }

  if (message.countryFilter?.length) {
    const countriesLabel = message.countryFilter.length === 1
      ? countryName(message.countryFilter[0])
      : `${message.countryFilter.length} pays`;
    parts.push(countriesLabel);
  }

  if (message.minAge != null || message.maxAge != null) {
    const age = message.minAge != null && message.maxAge != null
      ? `${message.minAge}–${message.maxAge} ans`
      : message.minAge != null
        ? `${message.minAge}+ ans`
        : `≤${message.maxAge} ans`;
    parts.push(age);
  }

  if (message.excludedRecipientDetails?.length) {
    parts.push(`-${message.excludedRecipientDetails.length} exclu(s)`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
};

type TProps = Pick<IMessageDetailModalProps, 'onResend'>;

const MessagesListTab = ({ onResend }: TProps) => {
  const { values: [page, limit] } = useQueryParams(['page', 'limit'], { page: 1, limit: 5 });
  const [selectedMessage, setSelectedMessage] = useState<IAdminMessage | null>(null);

  const { isLoading, messages, pagination } = useRequestGetAdminMessages({
    page: parseInt(String(page)),
    limit: parseInt(String(limit))
  });

  return (
    <>
      <div className='bg-white rounded-2xl shadow-soft border border-border overflow-hidden'>
        <div className='flex items-center justify-between px-4 py-3 border-b border-border'>
          <h3 className='font-semibold text-sm'>Historique</h3>
          {!isLoading && pagination && (
            <span className='text-xs text-muted-foreground'>{pagination.itemCount} msg</span>
          )}
        </div>

        {isLoading ? (
          <div className='divide-y divide-border'>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={cn('px-4 py-3 space-y-1.5 animate-pulse', i % 2 === 0 && 'bg-muted/5')}>
                <div className='flex items-center justify-between'>
                  <div className='h-2.5 bg-muted rounded w-24' />
                  <div className='h-4 bg-muted rounded w-12' />
                </div>
                <div className='h-3 bg-muted rounded w-4/5' />
                <div className='h-2.5 bg-muted rounded w-1/2' />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <p className='px-4 py-10 text-center text-sm text-muted-foreground'>Aucun message envoyé.</p>
        ) : (
          <div className='divide-y divide-border'>
            {messages.map((message, idx) => {
              const filters = filtersLabel(message);
              return (
                <button
                  key={message._id}
                  type='button'
                  onClick={() => setSelectedMessage(message)}
                  className={cn('w-full px-4 py-3 text-left space-y-1 hover:bg-muted/30 transition-colors', idx % 2 === 0 && 'bg-muted/5')}
                >
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-xs text-muted-foreground'>{dayjs(message.createdAt).format('DD MMM YYYY · HH:mm')}</span>
                    <Badge className={`text-[10px] py-0 leading-4 shrink-0 ${STATUS_CLASS[message.status]}`}>
                      {STATUS_LABEL[message.status]}
                    </Badge>
                  </div>
                  <p className='text-sm text-foreground leading-snug line-clamp-1'>{message.messageFR}</p>
                  <p className='text-xs text-muted-foreground'>
                    {audienceLabel(message.recipients)}{filters ? ` · ${filters}` : ''}
                  </p>
                </button>
              );
            })}
          </div>
        )}

        {!isLoading && (pagination?.pageCount ?? 0) > 1 && (
          <div className='px-4 py-3 border-t border-border'>
            <DataTablePagination pageCount={pagination?.pageCount} />
          </div>
        )}
      </div>

      <MessageDetailModal
        open={selectedMessage != null}
        message={selectedMessage}
        onClose={() => setSelectedMessage(null)}
        onResend={(message) => {
          setSelectedMessage(null);
          onResend(message);
        }}
      />
    </>
  );
};

export default MessagesListTab;
